import { useState } from 'react'
import { useParams, Link, useNavigate } from 'react-router'
import { trpc } from '@/config/trpc'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/shadcn/card'
import { Badge } from '@/components/ui/shadcn/badge'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/shadcn/tooltip'
import { MetricChart } from '@/components/analytics/metric-chart'
import {
  FacebookIcon,
  InstagramIcon,
  LinkedinIcon,
  TikTokIcon,
  YoutubeIcon,
} from '@/components/ui/icons/icons-new'
import {
  ArrowLeft,
  ExternalLink,
  Eye,
  Heart,
  MessageCircle,
  Share2,
  Bookmark,
  TrendingUp,
  AlertTriangle,
  Trash2,
  Users,
  MousePointerClick,
  Clock,
  Timer,
  Percent,
} from 'lucide-react'

const platformIcons = {
  instagram: InstagramIcon,
  linkedin: LinkedinIcon,
  tiktok: TikTokIcon,
  youtube: YoutubeIcon,
  facebook: FacebookIcon,
} as const

const platformLabels: Record<string, string> = {
  instagram: 'Instagram',
  linkedin: 'LinkedIn',
  tiktok: 'TikTok',
  youtube: 'YouTube',
  facebook: 'Facebook',
}

function formatNumber(n: number | null | undefined): string {
  if (n == null) return '-'
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`
  return n.toLocaleString()
}

const universalMetrics = [
  {
    key: 'views',
    label: 'Views',
    icon: Eye,
    tip: 'Total number of times the post was displayed (includes repeat views)',
  },
  { key: 'reach', label: 'Reach', icon: Users, tip: 'Unique accounts that saw the post' },
  { key: 'likes', label: 'Likes', icon: Heart, tip: 'Number of likes on the post' },
  {
    key: 'comments',
    label: 'Comments',
    icon: MessageCircle,
    tip: 'Number of comments on the post',
  },
  { key: 'shares', label: 'Shares', icon: Share2, tip: 'Number of times the post was shared' },
  {
    key: 'saves',
    label: 'Saves',
    icon: Bookmark,
    tip: 'Number of times the post was saved or bookmarked',
  },
  {
    key: 'clicks',
    label: 'Clicks',
    icon: MousePointerClick,
    tip: 'Total clicks on the post (links, media, profile, etc.)',
  },
  {
    key: 'engagementRate',
    label: 'Eng. Rate',
    icon: TrendingUp,
    tip: 'Ratio of engagements to views. Calculated as (likes + comments + shares) / views',
  },
  {
    key: 'watchTimeMinutes',
    label: 'Watch Time',
    icon: Clock,
    tip: 'Total minutes spent watching this video across all viewers',
  },
  {
    key: 'avgViewDurationSeconds',
    label: 'Avg Duration',
    icon: Timer,
    tip: 'Average time each viewer spent watching this video',
  },
  {
    key: 'avgViewPercentage',
    label: 'Avg View %',
    icon: Percent,
    tip: 'Average percentage of the video watched per view',
  },
] as const

/** Metrics we compute ourselves — not raw from the platform API */
const computedFields: Record<string, { universal: Set<string>; extra: Set<string> }> = {
  tiktok: { universal: new Set(['engagementRate']), extra: new Set() },
  youtube: { universal: new Set(['engagementRate']), extra: new Set() },
  facebook: { universal: new Set(['engagementRate']), extra: new Set() },
  linkedin: { universal: new Set(), extra: new Set(['clickThroughRate']) },
  instagram: { universal: new Set(), extra: new Set() },
}

/** Extra fields stored as 0-1 decimals that should display as percentages */
const rateExtraKeys = new Set([
  'cardClickRate',
  'annotationClickThroughRate',
  'nativeEngagementRate',
  'clickThroughRate',
  'thumbnailClickRate',
])

const extraTips: Record<string, string> = {
  // Instagram
  follows: 'New followers gained from this post',
  profileVisits: 'Profile visits driven by this post',
  totalInteractions: 'Sum of all interactions (native Instagram aggregate)',
  storyTapsForward: 'Number of taps to skip to the next story',
  storyTapsBack: 'Number of taps to go back to the previous story',
  storyExits: 'Number of times viewers left the story',
  // YouTube
  subscribersGained: 'New subscribers gained from this video',
  subscribersLost: 'Subscribers lost after watching this video',
  cardClickRate: 'Percentage of viewers who clicked an info card',
  annotationClickThroughRate: 'Percentage of viewers who clicked an annotation',
  thumbnailImpressions: 'Number of times the video thumbnail was shown to viewers',
  thumbnailClickRate: 'Percentage of thumbnail impressions that resulted in a view',
  engagedViews:
    'Views past initial seconds — for Shorts, views counts any play/replay while this counts meaningful watches',
  dislikes: 'Number of dislikes (hidden in YouTube UI but still tracked)',
  // LinkedIn
  uniqueImpressions: 'Unique accounts that saw the post (deduplicated)',
  nativeEngagementRate: 'Engagement rate as calculated by LinkedIn',
  clickThroughRate: 'Calculated as clicks / total impressions',
  // Facebook
  reactions: 'Total reactions across all types (like, love, etc.)',
  consumptions: 'Total clicks on the post (link clicks, photo views, etc.)',
}

function formatExtraLabel(key: string): string {
  return key
    .replace(/([A-Z])/g, ' $1')
    .replace(/^./, (s) => s.toUpperCase())
    .trim()
}

function formatMetricValue(key: string, value: number | null | undefined): string {
  if (value == null) return '-'
  if (key === 'engagementRate' || rateExtraKeys.has(key)) return `${(value * 100).toFixed(1)}%`
  if (key === 'avgViewPercentage') return `${value.toFixed(1)}%`
  if (key === 'watchTimeMinutes') {
    if (value >= 60) return `${(value / 60).toFixed(1)}h`
    return `${value.toFixed(1)}m`
  }
  if (key === 'avgViewDurationSeconds') {
    if (value >= 60) return `${Math.floor(value / 60)}m ${Math.round(value % 60)}s`
    return `${value.toFixed(0)}s`
  }
  return formatNumber(value)
}

export function AnalyticsPostDetailPage() {
  const { id } = useParams<{ id: string }>()
  const trackedPostId = Number(id)
  const navigate = useNavigate()
  const [confirmDelete, setConfirmDelete] = useState(false)

  const utils = trpc.useUtils()
  const removeMutation = trpc.trackedPost.remove.useMutation({
    onSuccess: () => {
      utils.analytics.overview.invalidate()
      utils.trackedPost.list.invalidate()
      navigate('/analytics')
    },
  })

  const { data, isLoading, error } = trpc.analytics.postDetail.useQuery(
    { trackedPostId },
    { enabled: !isNaN(trackedPostId) },
  )

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="border-primary/30 border-t-primary h-8 w-8 animate-spin rounded-full border-2" />
      </div>
    )
  }

  if (error || !data) {
    return (
      <div className="mx-auto max-w-5xl p-6">
        <p className="text-foreground font-medium">
          {error ? 'Failed to load post' : 'Post not found'}
        </p>
        {error && <p className="text-muted-foreground mt-1 text-sm">{error.message}</p>}
        <Link to="/analytics" className="text-primary mt-3 inline-block text-sm hover:underline">
          Back to Analytics
        </Link>
      </div>
    )
  }

  const { trackedPost, snapshots } = data
  const latest = snapshots[snapshots.length - 1]
  const PlatformIcon = platformIcons[trackedPost.platform as keyof typeof platformIcons]
  const nextSyncLabel = getNextSyncLabel(trackedPost.trackingPhase, trackedPost.lastSyncedAt)

  return (
    <div className="mx-auto w-full max-w-5xl p-6">
      {/* Back link */}
      <Link
        to="/analytics"
        className="text-muted-foreground hover:text-foreground mb-5 inline-flex items-center gap-1.5 text-sm transition-colors">
        <ArrowLeft size={14} />
        Analytics
      </Link>

      {/* Post header */}
      <Card className="mb-6">
        <CardContent className="flex gap-4 pt-1">
          {trackedPost.mediaS3Url && (
            <div className="bg-muted h-20 w-20 flex-shrink-0 overflow-hidden rounded-lg">
              <img src={trackedPost.mediaS3Url} alt="" className="h-full w-full object-cover" />
            </div>
          )}
          <div className="min-w-0 flex-1">
            <div className="mb-1.5 flex items-center gap-2">
              {PlatformIcon && (
                <PlatformIcon width={16} height={16} color="var(--muted-foreground)" />
              )}
              <span className="text-muted-foreground text-sm capitalize">
                {trackedPost.platform}
              </span>
              {trackedPost.mediaType && (
                <Badge variant="outline" className="text-[10px]">
                  {trackedPost.mediaType}
                </Badge>
              )}
            </div>
            <p className="text-foreground line-clamp-2 leading-snug">
              {trackedPost.caption || 'No caption'}
            </p>
            <div className="text-muted-foreground mt-2 flex items-center gap-3 text-xs">
              <span>
                {new Date(trackedPost.publishedAt).toLocaleDateString(undefined, {
                  month: 'long',
                  day: 'numeric',
                  year: 'numeric',
                })}
              </span>
              {trackedPost.permalink && (
                <a
                  href={trackedPost.permalink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary inline-flex items-center gap-1 hover:underline">
                  View original <ExternalLink size={11} />
                </a>
              )}
              {nextSyncLabel && <span className="text-muted-foreground/50">{nextSyncLabel}</span>}
            </div>

            {trackedPost.syncFailCount >= 3 && (
              <div className="mt-2 flex items-center gap-1.5 text-xs text-amber-600 dark:text-amber-400">
                <AlertTriangle size={12} />
                <span>
                  Sync failing ({trackedPost.syncFailCount} consecutive failures)
                  {!trackedPost.lastSyncedAt && ' — never synced successfully'}
                </span>
              </div>
            )}
          </div>

          {/* Delete button */}
          <div className="flex flex-shrink-0 flex-col items-end gap-1">
            {!confirmDelete ? (
              <button
                onClick={() => setConfirmDelete(true)}
                className="text-muted-foreground/50 hover:text-destructive rounded p-1.5 transition-colors"
                title="Remove tracked post">
                <Trash2 size={16} />
              </button>
            ) : (
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setConfirmDelete(false)}
                  className="text-muted-foreground text-xs hover:underline">
                  Cancel
                </button>
                <button
                  onClick={() => removeMutation.mutate({ trackedPostId })}
                  disabled={removeMutation.isPending}
                  className="bg-destructive text-destructive-foreground hover:bg-destructive/90 rounded px-2.5 py-1 text-xs font-medium disabled:opacity-50">
                  {removeMutation.isPending ? 'Removing...' : 'Confirm'}
                </button>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Latest metrics summary */}
      {latest && (
        <TooltipProvider delayDuration={200}>
          <div className="mb-6">
            <p className="text-muted-foreground mb-2 text-xs font-medium uppercase tracking-wider">
              Metrics
            </p>
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
              {universalMetrics
                .filter((m) => latest[m.key as keyof typeof latest] != null)
                .map((m) => {
                  const value = latest[m.key as keyof typeof latest] as number | null
                  const isComputed =
                    computedFields[trackedPost.platform]?.universal.has(m.key) ?? false
                  return (
                    <Tooltip key={m.key}>
                      <TooltipTrigger asChild>
                        <Card>
                          <CardContent className="space-y-0 pt-1">
                            <div className="flex items-center gap-1.5">
                              <m.icon size={12} className="text-muted-foreground/50" />
                              <p className="text-muted-foreground text-[10px] uppercase tracking-wider">
                                {m.label}
                              </p>
                              {isComputed && (
                                <span className="rounded bg-amber-100 px-1 py-px text-[9px] font-medium text-amber-700 dark:bg-amber-900/30 dark:text-amber-400">
                                  CALC
                                </span>
                              )}
                            </div>
                            <p className="text-foreground text-xl font-semibold tabular-nums">
                              {formatMetricValue(m.key, value)}
                            </p>
                          </CardContent>
                        </Card>
                      </TooltipTrigger>
                      <TooltipContent>
                        <p className="max-w-56">{m.tip}</p>
                      </TooltipContent>
                    </Tooltip>
                  )
                })}
            </div>
          </div>

          {/* Platform-specific extras */}
          {latest.platformExtra &&
            Object.entries(latest.platformExtra).some(([, v]) => v != null) && (
              <div className="mb-6">
                <p className="text-muted-foreground mb-2 text-xs font-medium uppercase tracking-wider">
                  {platformLabels[trackedPost.platform] ?? trackedPost.platform}-specific
                </p>
                <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
                  {Object.entries(latest.platformExtra)
                    .filter(([, v]) => v != null)
                    .map(([key, value]) => {
                      const isComputed =
                        computedFields[trackedPost.platform]?.extra.has(key) ?? false
                      const tip = extraTips[key]
                      return (
                        <Tooltip key={key}>
                          <TooltipTrigger asChild>
                            <Card>
                              <CardContent className="space-y-0 pt-1">
                                <div className="flex items-center gap-1.5">
                                  <p className="text-muted-foreground text-[10px] uppercase tracking-wider">
                                    {formatExtraLabel(key)}
                                  </p>
                                  {isComputed && (
                                    <span className="rounded bg-amber-100 px-1 py-px text-[9px] font-medium text-amber-700 dark:bg-amber-900/30 dark:text-amber-400">
                                      CALC
                                    </span>
                                  )}
                                </div>
                                <p className="text-foreground text-xl font-semibold tabular-nums">
                                  {formatMetricValue(key, value as number)}
                                </p>
                              </CardContent>
                            </Card>
                          </TooltipTrigger>
                          {tip && (
                            <TooltipContent>
                              <p className="max-w-56">{tip}</p>
                            </TooltipContent>
                          )}
                        </Tooltip>
                      )
                    })}
                </div>
              </div>
            )}
        </TooltipProvider>
      )}

      {/* Chart */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-semibold">Performance Over Time</CardTitle>
        </CardHeader>
        <CardContent>
          <MetricChart snapshots={snapshots} />
        </CardContent>
      </Card>
    </div>
  )
}

const SYNC_INTERVALS: Record<string, number> = {
  hot: 6 * 60 * 60 * 1000,
  warm: 24 * 60 * 60 * 1000,
  cool: 7 * 24 * 60 * 60 * 1000,
}

function getNextSyncLabel(phase: string, lastSyncedAt: string | Date | null): string | null {
  if (phase === 'archived') return 'Sync stopped (archived)'
  const interval = SYNC_INTERVALS[phase]
  if (!interval || !lastSyncedAt) return null
  const lastSync = new Date(lastSyncedAt).getTime()
  const nextSync = lastSync + interval
  const remaining = nextSync - Date.now()
  if (remaining <= 0) return 'Sync pending'
  const hours = Math.ceil(remaining / (60 * 60 * 1000))
  if (hours <= 1) return 'Next sync in <1h'
  if (hours < 24) return `Next sync in ~${hours}h`
  const days = Math.ceil(hours / 24)
  return `Next sync in ~${days}d`
}
